/**
 * useStreamKeepalive Hook
 *
 * Automatically sends keepalive signals to backend RTSP streams to prevent
 * premature termination. Backend kills streams after 12 seconds of no keepalive,
 * so this hook sends signals every 3 seconds by default.
 *
 * Usage:
 *   const streamId = "my-stream-123";
 *   useStreamKeepalive(streamId); // Starts sending keepalives every 3s
 *
 * @see electron/main.ts KEEPALIVE_TIMEOUT = 12000ms
 */

import { useEffect } from 'react';

interface UseStreamKeepaliveOptions {
  /**
   * Keepalive interval in milliseconds
   * @default 3000 (3 seconds - well under the 12s backend timeout)
   */
  intervalMs?: number;

  /**
   * Enable debug logging
   * @default false
   */
  debug?: boolean;
}

/**
 * Hook to automatically send keepalive signals for RTSP streams
 *
 * @param streamId - The ID of the active stream (null/undefined = no keepalive)
 * @param options - Configuration options
 */
export const useStreamKeepalive = (
  streamId: string | null | undefined,
  options: UseStreamKeepaliveOptions = {}
) => {
  const { intervalMs = 3000, debug = false } = options;

  useEffect(() => {
    // Don't start keepalive if no stream ID or not in Electron
    if (!streamId) {
      if (debug) console.log('[useStreamKeepalive] No stream ID, skipping keepalive');
      return;
    }

    if (typeof window === 'undefined' || !window.electronAPI?.keepaliveIPCameraStream) {
      if (debug) console.log('[useStreamKeepalive] Not in Electron environment');
      return;
    }

    const keepalive = async () => {
      try {
        const result = await window.electronAPI.keepaliveIPCameraStream(streamId);
        if (debug && !result.success) {
          console.warn('[useStreamKeepalive] Keepalive failed:', result.error);
        } else if (debug) {
          console.log(`[useStreamKeepalive] Keepalive sent for stream ${streamId}`);
        }
      } catch (error) {
        if (debug) {
          console.error('[useStreamKeepalive] Keepalive error:', error);
        }
      }
    };

    // Send initial keepalive immediately
    keepalive();

    // Then send periodically
    const timer = setInterval(keepalive, intervalMs);

    if (debug) {
      console.log(`[useStreamKeepalive] Started keepalive for stream ${streamId} (interval: ${intervalMs}ms)`);
    }

    // Cleanup on unmount or when streamId changes
    return () => {
      clearInterval(timer);
      if (debug) {
        console.log(`[useStreamKeepalive] Stopped keepalive for stream ${streamId}`);
      }
    };
  }, [streamId, intervalMs, debug]);
};
